import React, { useState } from "react";
import { Boxes, Workflow, X, ArrowRight, Bot } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { motion, AnimatePresence } from "framer-motion";
import cobrancaVideo from "@/assets/cobranca-demo.mp4";
import FlowEnvironment from "@/components/flow/FlowEnvironment";

export default function AIWorkflowShowcase() {
  const [showVideo, setShowVideo] = useState(false);
  const [showBox, setShowBox] = useState(false);
  const [activeFlow, setActiveFlow] = useState<string | null>("cobranca");

  const handleFlowClick = (flowName: string) => {
    setActiveFlow(flowName);
    setShowVideo(false);
    setShowBox(false);
  };

  return (
    <div className="relative mx-auto max-w-6xl lg:-mt-52 mt-0 z-20 pb-1 px-4">
      <div className="absolute inset-0 -z-10 rounded-[2rem] border border-[#7C3AED]/30 shadow-[0_8px_30px_-12px_rgba(124,58,237,0.25),0_12px_45px_-15px_rgba(0,188,212,0.15)]" />

      <Card className="relative border border-primary/10 bg-gradient-to-b from-[#0a0018]/90 to-[#090013]/70 backdrop-blur-2xl rounded-3xl overflow-hidden">
        <CardContent className="p-6 md:p-8">
          <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
            {/* ================== NODES LIST ================== */}
            <div className="flex flex-col gap-4">
              {/* Button 1: Cobrança via WhatsApp */}
              <button
                onClick={() => handleFlowClick("cobranca")}
                className={`group flex flex-col items-start justify-center border rounded-2xl shadow-[0_0_15px_rgba(124,58,237,0.4)] transition-all duration-300 p-4 h-auto md:h-[115px] w-full text-left ${activeFlow === "cobranca"
                  ? "border-primary bg-primary/10"
                  : "border-primary/60 bg-zinc-900/80 hover:bg-zinc-800/80"
                  }`}
              >
                <div className="flex items-center gap-2">
                  <Boxes className="h-4 w-4 text-muted-foreground" />
                  <span className="font-semibold text-sm text-primary">
                    Cobrança via WhatsApp
                  </span>
                </div>

                <p className="text-xs text-muted-foreground mt-1 leading-snug">
                  Fluxo automatizado de cobrança e notificações.
                </p>

                <div className="flex items-center gap-1 mt-2 text-[11px] text-primary opacity-90">
                  <Workflow className="h-3 w-3 animate-pulse text-primary" />{" "}
                  Flow active
                </div>
              </button>

              {/* Button 2: Integração IA */}
              <button
                onClick={() => handleFlowClick("ia_integration")}
                className={`group flex flex-col items-start justify-center border rounded-2xl shadow-[0_0_15px_rgba(124,58,237,0.4)] transition-all duration-300 p-4 h-auto md:h-[115px] w-full text-left ${activeFlow === "ia_integration"
                  ? "border-primary bg-primary/10"
                  : "border-primary/60 bg-zinc-900/80 hover:bg-zinc-800/80"
                  }`}
              >
                <div className="flex items-center gap-2">
                  <Bot className="h-4 w-4 text-muted-foreground" />
                  <span className="font-semibold text-sm text-primary leading-tight">
                    Integração com Inteligência Artificial e LLMs
                  </span>
                </div>

                <p className="text-xs text-muted-foreground mt-1 leading-snug">
                  Otimizações de acordo com suas demandas.
                </p>

                <div className="flex items-center gap-1 mt-2 text-[11px] text-primary opacity-90">
                  <Workflow className="h-3 w-3 animate-pulse text-primary" />{" "}
                  Flow active
                </div>
              </button>
            </div>

            {/* ================== PREVIEW / FLOW AREA ================== */}
            <div className="relative rounded-2xl border border-zinc-700/40 bg-zinc-900/30 overflow-hidden flex flex-col items-center justify-center">
              {/* GRID BACKGROUND */}
              <div className="absolute inset-0 z-0 pointer-events-none bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.08)_1px,transparent_0)] bg-[size:20px_20px]" />

              <div className="relative w-full h-[260px] sm:h-[320px] md:h-[420px] lg:h-[480px] flex items-center justify-center">

                {/* 1. SHOW IMAGE if no flow active and no video */}
                {!activeFlow && !showVideo && (
                  <img
                    src="/assets/fluxo1.png"
                    alt="Flow Preview"
                    className="relative z-10 max-w-full max-h-full object-contain rounded-xl shadow-lg border border-zinc-700/40 transition-all duration-500"
                  />
                )}

                {/* 2. SHOW FLOW if active */}
                {activeFlow && (
                  <div className="w-full h-full z-20">
                    <FlowEnvironment flowType={activeFlow as 'cobranca' | 'ia_integration'} />
                  </div>
                )}

                {/* 3. SHOW VIDEO if active */}
                {showVideo && (
                  <video
                    src={cobrancaVideo}
                    controls
                    className="relative z-20 w-full h-full object-contain rounded-xl border border-primary/40 shadow-lg shadow-primary/20"
                  />
                )}
              </div>

              {/* BOTÃO ENTENDA COMO FUNCIONA (Only show if not in flow mode) */}
              {!activeFlow && (
                <div className="absolute bottom-3 flex justify-center w-full hidden md:flex z-30">
                  <Button
                    size="lg"
                    onClick={() => setShowBox((prev) => !prev)}
                    className="relative overflow-hidden border border-white/60 text-white text-base px-8 py-5 rounded-full font-semibold tracking-wide 
                    bg-transparent backdrop-blur-sm transition-all duration-500 shadow-[0_0_12px_rgba(168,85,247,0.4)] hover:shadow-[0_0_20px_rgba(168,85,247,0.6)]
                    hover:bg-white/5 group animate-pulse-smooth"
                  >
                    Entenda como funciona
                    <ArrowRight className="ml-2 transition-transform group-hover:translate-x-1" />
                  </Button>
                </div>
              )}

              {/* MODAL DE DETALHES */}
              <AnimatePresence>
                {showBox && (
                  <motion.div
                    initial={{ y: 150, opacity: 0, scale: 0.95 }}
                    animate={{ y: 0, opacity: 1, scale: 1 }}
                    exit={{ y: 150, opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.5 }}
                    className="absolute inset-x-0 bottom-0 mx-auto w-[96%] bg-gradient-to-br from-[#0a0014]/90 via-[#340D5C]/85 to-black/85 backdrop-blur-xl border border-[#7C3AED]/60 text-white px-14 py-12 rounded-3xl shadow-[0_-8px_35px_rgba(124,58,237,0.45)] max-w-[1250px] z-30"
                  >
                    <button
                      onClick={() => setShowBox(false)}
                      className="absolute top-5 right-6 text-white/70 hover:text-primary"
                    >
                      <X className="w-6 h-6" />
                    </button>

                    <div className="text-center mb-12">
                      <h2 className="text-2xl font-bold text-primary mb-2">
                        Detalhes do Fluxo
                      </h2>
                      <p className="text-sm text-zinc-400">
                        Diagnóstico e solução detalhada do processo ativo
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                      <div className="rounded-2xl border border-[rgba(124,58,237,0.4)] bg-[#1B1624]/95 backdrop-blur-md p-8 hover:border-[#7C3AED]/80">
                        <h3 className="font-semibold text-xl text-primary mb-4">
                          PROBLEMA
                        </h3>
                        <p className="text-zinc-200">O vídeo não está sendo reproduzido.</p>
                      </div>

                      <div className="rounded-2xl border border-[rgba(124,58,237,0.4)] bg-[#1B1624]/95 backdrop-blur-md p-8 hover:border-[#7C3AED]/80">
                        <h3 className="font-semibold text-xl text-primary mb-4">
                          SOLUÇÃO
                        </h3>
                        <p className="text-zinc-200">Verifique se o arquivo MP4 está em src/assets e se o import (cobranca-demo.mp4) está correto.</p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
